import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Home, ClipboardCheck, Target, MessageCircleHeart, Wind, CircleDot, User, ScrollText } from "lucide-react";
import SOSButton from "@/components/SOSButton";

const NAV = [
  { to: "/dashboard", label: "Kāinga", icon: Home, testid: "nav-home" },
  { to: "/checkin", label: "Tirohia", icon: ClipboardCheck, testid: "nav-checkin" },
  { to: "/wheel", label: "Wīra", icon: CircleDot, testid: "nav-wheel" },
  { to: "/surveys", label: "Rangahau", icon: ScrollText, testid: "nav-surveys" },
  { to: "/toolkit", label: "Hā", icon: Wind, testid: "nav-toolkit" },
  { to: "/companion", label: "Kōrero", icon: MessageCircleHeart, testid: "nav-companion" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen whenua-gradient pb-24 md:pb-0">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-bone/75 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <NavLink to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <Logo />
            <div className="leading-tight">
              <div className="font-serif text-lg sm:text-xl text-ngahere">Mā te mātau ki ora</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hidden sm:block">whiria te muka aronga</div>
            </div>
          </NavLink>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm transition-all duration-200 ${
                    isActive ? "bg-ngahere text-muka" : "text-charcoal/70 hover:text-ngahere hover:bg-muka"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => nav("/profile")}
              data-testid="profile-button"
              className="w-10 h-10 rounded-full bg-ngahere text-muka flex items-center justify-center font-serif text-base hover:scale-105 transition-transform"
              aria-label="Profile"
            >
              {user?.display_name?.[0]?.toUpperCase() || <User size={16} />}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-muka/95 backdrop-blur border-t border-border z-30" data-testid="bottom-nav">
        <div className="grid grid-cols-6 max-w-md mx-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2.5 text-[10px] ${
                  isActive ? "text-ngahere" : "text-charcoal/55"
                }`
              }
            >
              <n.icon size={20} strokeWidth={1.6} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      <SOSButton />
    </div>
  );
}

function Logo() {
  return (
    <svg width="36" height="36" viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <circle cx="20" cy="20" r="18" stroke="#2B4522" strokeWidth="1.2" />
      <path d="M20 4 L20 36" stroke="#8B3A33" strokeWidth="1" strokeDasharray="2 3" />
      <path d="M4 20 L36 20" stroke="#8B3A33" strokeWidth="1" strokeDasharray="2 3" />
      <path d="M8 8 L32 32 M32 8 L8 32" stroke="#9C6644" strokeWidth="0.8" opacity="0.6" />
      <circle cx="20" cy="20" r="3" fill="#2B4522" />
    </svg>
  );
}
